import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'hooks';
import { useDimension, useIsHeavyReport } from 'hooks';
import { isProgrammaticAccount } from 'modules/account-management/accountType';
import { useReportEntityIds } from 'modules/campaigns/hooks/useReportEntityIds';
import { useReportEntityType } from 'modules/campaigns/hooks/useReportEntityType';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useAppEventContext } from 'modules/taboola-common-frontend-modules/app-events-aggregator';
import { permissionsSelector } from 'modules/taboola-common-frontend-modules/authentication';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication/hooks';
import { useChatAgentContext } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useChatAgentContext';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useMultiLayerDrawerEnabled } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerDrawerEnabled';
import { CREATIVE_REPORT_ID } from '../../config/reports/creative/creativeReportId';
import { useSelectedReportId } from '../../hooks';
import { useReportComfortableViewParam } from '../../hooks/useReportComfortableViewParam';
import { useReportFiltersStatus } from '../../hooks/useReportFiltersStatus';
import { GridContext } from '../GridContextProvider/GridContextProvider';

const emptyReportConfig = {};
const CampaignGridContextProvider = ({
    children,
    reportConfig = emptyReportConfig,
    saveColumnStateOnGridChange,
    selectedAccountConfig,
    reportPresetName,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMultiLayerDrawerEnabled = useMultiLayerDrawerEnabled();
    const userPermissions = useSelector(permissionsSelector);
    const intl = useIntl();
    const { inlineEditPermissions } = reportConfig;
    const hasInlineEditAccess = usePermissions(inlineEditPermissions);
    const isHeavyReport = useIsHeavyReport();
    const [selectedReport] = useSelectedReportId();
    const selectedAccount = useAccount();
    const {
        id: accountNumericId,
        isActive: isActiveAccount,
        currency,
        timeZoneName,
        accountName,
        type: accountType,
    } = selectedAccount;
    const [isFilterActive] = useReportFiltersStatus();
    const [selectedEntityType] = useReportEntityType();
    const [selectedEntityIds] = useReportEntityIds();
    const isGridEditDisabled =
        !isActiveAccount ||
        !hasInlineEditAccess ||
        isHeavyReport ||
        (isProgrammaticAccount(selectedAccount) && selectedReport === CREATIVE_REPORT_ID);
    const [cellToEdit, setCellToEdit] = useState(null);
    const { registerGrid } = useChatAgentContext();
    const { push: pushAppEvent } = useAppEventContext();
    const [isComfortableViewEnabled] = useReportComfortableViewParam();
    const [dimension] = useDimension();
    const onColumnStateChanged = useCallback(
        props => saveColumnStateOnGridChange({ ...props, reportId: selectedReport, reportPresetName }),
        [selectedReport, reportPresetName, saveColumnStateOnGridChange]
    );
    const value = useMemo(
        () => ({
            isGridEditDisabled,
            isFilterActive,
            cellToEdit,
            dispatch,
            navigate,
            isMultiLayerDrawerEnabled,
            onColumnStateChanged,
            userPermissions,
            intl,
            selectedAccountConfig,
            currency,
            timeZoneName,
            accountName,
            accountType,
            notPinnedLabel: (
                <FormattedMessage id="app.campaigns.reports.grid.column.menu.unpin" defaultMessage="Unpin" />
            ),
            leftPinnedLabel: (
                <FormattedMessage id="app.campaigns.reports.grid.column.menu.pin.left" defaultMessage="Pin Left" />
            ),
            pushAppEvent,
            selectedEntityIds,
            selectedEntityType,
            accountNumericId,
            reportPresetName,
            reportId: selectedReport,
            isComfortableViewEnabled,
            dimension,
        }),
        [
            isGridEditDisabled,
            isFilterActive,
            cellToEdit,
            dispatch,
            isMultiLayerDrawerEnabled,
            onColumnStateChanged,
            userPermissions,
            selectedAccountConfig,
            intl,
            currency,
            timeZoneName,
            accountName,
            accountType,
            pushAppEvent,
            navigate,
            selectedEntityIds,
            selectedEntityType,
            accountNumericId,
            reportPresetName,
            selectedReport,
            isComfortableViewEnabled,
            dimension,
        ]
    );

    useEffect(() => registerGrid({ setCellToEdit }), [registerGrid]);

    return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
};

CampaignGridContextProvider.propTypes = { reportConfig: PropTypes.object.isRequired };

export default CampaignGridContextProvider;
